import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, Pen, Brush, Redo2, Type, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";
import { useAuthImage } from "@/components/admin/todos/AuthImage";
import { err } from "@/components/admin/todos/TodosTab";

const COLORS = ["#111111", "#ffffff", "#e53935", "#fdd835", "#43a047", "#1e88e5"];
const TOOLS = { pen: { label: "Pen", icon: Pen, min: 1, max: 12, def: 3 }, brush: { label: "Brush", icon: Brush, min: 10, max: 60, def: 24 }, text: { label: "Text", icon: Type, min: 14, max: 120, def: 40 } };
const FONT = (px) => `700 ${px}px Outfit, Helvetica, Arial, sans-serif`;

const drawStroke = (ctx, s, k) => {
  const pts = s.points;
  ctx.strokeStyle = s.color;
  ctx.lineWidth = s.size * k;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.globalAlpha = s.tool === "brush" ? 0.85 : 1;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  if (pts.length === 1) ctx.lineTo(pts[0][0] + 0.01, pts[0][1]);
  for (let i = 1; i < pts.length - 1; i += 1) ctx.quadraticCurveTo(pts[i][0], pts[i][1], (pts[i][0] + pts[i + 1][0]) / 2, (pts[i][1] + pts[i + 1][1]) / 2);
  if (pts.length > 1) ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
  ctx.stroke();
  ctx.globalAlpha = 1;
};

const drawText = (ctx, t, k) => {
  ctx.font = FONT(t.size * k);
  ctx.textBaseline = "top";
  ctx.fillStyle = t.color;
  ctx.shadowColor = t.color === "#ffffff" || t.color === "#fdd835" ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.35)";
  ctx.shadowBlur = 2 * k;
  ctx.fillText(t.text || " ", t.x, t.y);
  ctx.shadowBlur = 0;
};

const textBox = (ctx, t, k) => {
  ctx.font = FONT(t.size * k);
  return { x: t.x, y: t.y, w: Math.max(ctx.measureText(t.text || " ").width, 20 * k), h: t.size * k * 1.2 };
};

export const ImageMarkup = ({ todoId, att, onClose, onSaved }) => {
  const path = `/todos/${todoId}/attachments/${att.id}`;
  const url = useAuthImage(path, att.edited_at || att.created_at);
  const [img, setImg] = useState(null);
  const canvas = useRef(null);
  const wrap = useRef(null);
  const [fit, setFit] = useState(1);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#e53935");
  const [sizes, setSizes] = useState({ pen: 3, brush: 24, text: 40 });
  const [hist, setHist] = useState({ past: [], present: [], future: [] });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const live = useRef(null);
  const drag = useRef(null);
  const draftInput = useRef(null);
  const k = img ? Math.max(img.naturalWidth, img.naturalHeight) / 1200 : 1;

  useEffect(() => {
    if (!url) return;
    const im = new Image();
    im.onload = () => setImg(im);
    im.src = url;
  }, [url]);

  useEffect(() => {
    if (!img || !wrap.current) return undefined;
    const measure = () => { const b = wrap.current.getBoundingClientRect(); setFit(Math.min(b.width / img.naturalWidth, b.height / img.naturalHeight)); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, [img]);

  const render = useCallback((withDraft = true) => {
    const c = canvas.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    hist.present.forEach((el) => (el.type === "text" ? drawText(ctx, el, k) : drawStroke(ctx, el, k)));
    if (live.current) drawStroke(ctx, live.current, k);
    if (withDraft && draft) {
      drawText(ctx, draft, k);
      const b = textBox(ctx, draft, k);
      ctx.setLineDash([6 * k, 4 * k]);
      ctx.strokeStyle = "#1e88e5";
      ctx.lineWidth = 1.5 * k;
      ctx.strokeRect(b.x - 4 * k, b.y - 4 * k, b.w + 8 * k, b.h + 8 * k);
      ctx.setLineDash([]);
    }
  }, [img, hist.present, draft, k]);
  useEffect(() => { render(); }, [render]);

  const commit = (present) => setHist((h) => ({ past: [...h.past, h.present], present, future: [] }));
  const undo = () => setHist((h) => (h.past.length ? { past: h.past.slice(0, -1), present: h.past[h.past.length - 1], future: [h.present, ...h.future] } : h));
  const redo = () => setHist((h) => (h.future.length ? { past: [...h.past, h.present], present: h.future[0], future: h.future.slice(1) } : h));
  const pos = (e) => { const b = canvas.current.getBoundingClientRect(); return [(e.clientX - b.left) * (img.naturalWidth / b.width), (e.clientY - b.top) * (img.naturalHeight / b.height)]; };
  const hit = (p, t) => { const b = textBox(canvas.current.getContext("2d"), t, k); return p[0] >= b.x - 6 * k && p[0] <= b.x + b.w + 6 * k && p[1] >= b.y - 6 * k && p[1] <= b.y + b.h + 6 * k; };

  const commitDraft = () => {
    if (draft?.text.trim()) commit([...hist.present, { type: "text", x: draft.x, y: draft.y, text: draft.text, color: draft.color, size: draft.size }]);
    setDraft(null);
  };

  const down = (e) => {
    e.preventDefault();
    try { canvas.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const p = pos(e);
    if (tool === "text") {
      if (draft && hit(p, draft)) { drag.current = { kind: "draft", dx: p[0] - draft.x, dy: p[1] - draft.y }; return; }
      const idx = hist.present.map((el, i) => [el, i]).reverse().find(([el]) => el.type === "text" && hit(p, el));
      if (idx) {
        const [el, i] = idx;
        setHist((h) => ({ past: [...h.past, h.present], present: h.present, future: [] }));
        drag.current = { kind: "el", i, dx: p[0] - el.x, dy: p[1] - el.y };
        return;
      }
      commitDraft();
      setDraft({ x: p[0], y: p[1] - (sizes.text * k) / 2, text: "", color, size: sizes.text });
      setTimeout(() => draftInput.current?.focus(), 0);
      return;
    }
    if (draft) commitDraft();
    live.current = { type: "stroke", tool, color, size: sizes[tool], points: [p] };
    render();
  };
  const move = (e) => {
    const p = img && canvas.current ? pos(e) : null;
    if (!p) return;
    if (drag.current?.kind === "draft") { setDraft((d) => ({ ...d, x: p[0] - drag.current.dx, y: p[1] - drag.current.dy })); return; }
    if (drag.current?.kind === "el") {
      const { i, dx, dy } = drag.current;
      setHist((h) => ({ ...h, present: h.present.map((el, j) => (j === i ? { ...el, x: p[0] - dx, y: p[1] - dy } : el)) }));
      return;
    }
    if (!live.current) return;
    const list = e.nativeEvent.getCoalescedEvents ? e.nativeEvent.getCoalescedEvents() : [];
    (list.length ? list : [e]).forEach((ev) => live.current.points.push(pos(ev)));
    render();
  };
  const up = () => {
    drag.current = null;
    if (live.current) { const s = live.current; live.current = null; commit([...hist.present, s]); }
  };

  const setSize = (v) => { setSizes((s) => ({ ...s, [tool]: v })); if (tool === "text" && draft) setDraft((d) => ({ ...d, size: v })); };
  const pickColor = (c) => { setColor(c); if (draft) setDraft((d) => ({ ...d, color: c })); };

  const save = async () => {
    const finalEls = draft?.text.trim() ? [...hist.present, { type: "text", ...draft }] : hist.present;
    setDraft(null);
    setSaving(true);
    const c = canvas.current;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, c.width, c.height);
    finalEls.forEach((el) => (el.type === "text" ? drawText(ctx, el, k) : drawStroke(ctx, el, k)));
    const mime = att.content_type === "image/png" ? "image/png" : "image/jpeg";
    const blob = await new Promise((res) => c.toBlob(res, mime, 0.92));
    const fd = new FormData();
    fd.append("file", blob, att.original_name.replace(/\.[^.]+$/, "") + (mime === "image/png" ? ".png" : ".jpg"));
    try { const { data } = await adminApi.put(path, fd); toast.success("Markup saved — the image was replaced"); onSaved(data); } catch (e) { err(e); setSaving(false); }
  };

  const T = TOOLS[tool];
  const draftCss = draft && canvas.current ? { left: draft.x * fit, top: (draft.y + draft.size * k * 1.25) * fit + 6 } : null;
  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent aria-describedby={undefined} className="td-editor" data-testid="markup-editor" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogTitle className="sr-only">Mark up image</DialogTitle>
        <div className="td-editor__bar">
          <div className="td-editor__group" role="group" aria-label="Tools">
            {Object.entries(TOOLS).map(([key, v]) => { const I = v.icon; return <button key={key} className={`td-tool ${tool === key ? "is-on" : ""}`} onClick={() => { if (key !== "text") commitDraft(); setTool(key); }} data-testid={`markup-tool-${key}`}><I size={15} /> {v.label}</button>; })}
          </div>
          <div className="td-editor__group" role="group" aria-label="Colors">
            {COLORS.map((c) => <button key={c} className={`td-swatch ${color === c ? "is-on" : ""}`} style={{ background: c }} onClick={() => pickColor(c)} aria-label={`Color ${c}`} data-testid={`markup-color-${c.slice(1)}`} />)}
            <input type="color" className="td-swatch td-swatch--custom" value={color} onChange={(e) => pickColor(e.target.value)} aria-label="Custom color" data-testid="markup-color-custom" />
          </div>
          <label className="td-editor__size"><span>Size</span><input type="range" min={T.min} max={T.max} value={sizes[tool]} onChange={(e) => setSize(Number(e.target.value))} data-testid="markup-size" /><b>{sizes[tool]}</b></label>
          <div className="td-editor__group">
            <button className="td-tool" onClick={undo} disabled={!hist.past.length} data-testid="markup-undo"><Undo2 size={15} /> Undo</button>
            <button className="td-tool" onClick={redo} disabled={!hist.future.length} data-testid="markup-redo"><Redo2 size={15} /> Redo</button>
            <button className="td-tool" onClick={() => { setDraft(null); commit([]); }} disabled={!hist.present.length && !draft} data-testid="markup-clear"><Eraser size={15} /> Clear</button>
          </div>
        </div>
        <div className="td-editor__stage" ref={wrap}>
          {!img ? <p className="hx-muted" style={{ color: "#ccc" }}>Loading image…</p> : (
            <div className="td-editor__canvaswrap" style={{ width: img.naturalWidth * fit, height: img.naturalHeight * fit }}>
              <canvas ref={canvas} width={img.naturalWidth} height={img.naturalHeight} className={`td-editor__canvas td-editor__canvas--${tool}`}
                style={{ width: img.naturalWidth * fit, height: img.naturalHeight * fit }} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="markup-canvas" />
              {draft && draftCss && (
                <div className="td-editor__draft" style={draftCss}>
                  <input ref={draftInput} value={draft.text} placeholder="Type text…" onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") commitDraft(); if (e.key === "Escape") setDraft(null); }} data-testid="markup-text-input" />
                  <button onClick={commitDraft} data-testid="markup-text-done">Done</button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="td-editor__foot">
          <span className="td-editor__hint">{tool === "text" ? "Click to place text · drag text to move it" : "Draw with mouse, pen or finger"} · saved at full size ({img ? `${img.naturalWidth}×${img.naturalHeight}` : "…"}) and replaces the original</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button className="hx-btn hx-btn--ghost hx-btn--sm" disabled={saving} onClick={onClose} data-testid="markup-cancel">Cancel</button>
            <button className="hx-btn hx-btn--sm" disabled={saving || !img || (!hist.present.length && !draft?.text)} onClick={save} data-testid="markup-save">{saving ? "Saving…" : "Save"}</button>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
